import { Layout } from 'components/common/layout/Layout';
import { gql, useQuery } from 'urql';
import { CustomSEO } from '../../../components/common/layout/HeadMeta';
import { ActivationSection } from '../../../components/partners-new/ind-partners/ActivationSection';
import { CalendarSection } from '../../../components/partners-new/ind-partners/CalendarSection';
import { PartDescr } from '../../../components/partners-new/ind-partners/PartDescr';
import { PartnersHeaderTabs } from '../../../components/partners-new/ind-partners/PartnersHeaderTabs';
import { QuoteSection } from '../../../components/partners-new/ind-partners/QuoteSection';
import { ShowcaseIcon } from '../../../components/svg/event-icons/ShowcaseIcon';
import { PanelIcon } from '../../../components/svg/PanelIcon';
import { OrgIhseaIndexDocument, OrgIhseaIndexQuery } from '../../../generated/spin-graphql';
import { UrqlSSGForPage } from '../../../server/utils/serverUtils/ssg.utils';

gql`
    query OrgIhseaIndex {
        espin_org_month(where: { org: { definition: { _eq: "ihsea_fed" } } }) {
            month
            total_days
            start_day
            month_of_year
            days {
                date
                title
                time
                game_def
                game {
                    game_definition
                }
            }
        }
    }
`;

export default function IhseaHome() {
    const title = 'IHSEA Partner';
    const description = 'Your home for Stay Plugged In & IHSEA esports content & scholarships.';
    const seoOverride: CustomSEO = {
        title: title,
        description: description,
    };

    const [result] = useQuery<OrgIhseaIndexQuery>({ query: OrgIhseaIndexDocument });
    const months = result?.data?.espin_org_month ?? [];

    const descrOne = `We are excited to partner with Illinois High School Esports Association (IHSEA). The partnership expands IHSEA’s collegiate recruitment offerings and allows the scholastic-oriented nonprofit organization to provide their players with a recruitment organizer at the high school level. Here at Stay Plugged In, we are excited to help students from Illinois find more collegiate opportunities through our platform and events.`;

    const descrAct1 =
        'Stay Plugged In & IHSEA will host two virtual recruiting showcases during the academic year of 2022-23. These virtual showcases are designed to highlight competitive matches between players who participate in IHSEA. With hundreds of colleges on the platform, there are an abundance of opportunities for students looking to gain collegiate scholarships.';

    const items = [
        'Fall Showcase: October 8th',
        'Valorant, Fortnite & League of Legends',
        'Swiss format with guaranteed three matches',
        'Competition begins at 2 PM CT',
        'Hosted online, sign up link is below!',
    ];

    const items2 = [
        'Spring Showcase: February 18th',
        'Rocket League, Overwatch & Smash: Ultimate',
        'Swiss format with guaranteed three matches',
        'Competition begins at 2 PM CT',
        'Hosted online',
    ];

    const descrPanel = `Over the course of the academic year, IHSEA & SPIN will be hosting educational panels to discuss esports recruiting opportunities and how to best purse them. Topics will range from how to best use the Stay Plugged In platform to tips & tricks for recruiting. The number of esports scholarship opportunities is incredible, where just for the Class of 2022, SPIN faciliated over $15 million in scholarships through our platform. See below for when we will be hosting a panel or town hall for your game.`;

    const itemsPanel = [
        'August 31 | Kick-Off with IHSEA Coaches',
        'September 14 | Valorant Kick-Off',
        'October 19 | Valorant Town-Hall',
        'October 19 | IHSEA Coaches & Parents Town Hall',
        'October 26 | League of Legends & Fortnite Kick-Off',
        'November 30 | Fornite Town Hall',
        'December 7 | Smash: Ultimate Kick-Off',
    ];

    const itemsPanel2 = [
        'February 1 |  Smash: Ultimate & League of Legends Town Hall',
        'February 8 |  Rocket League & Overwatch Kick-Off',
        'March 8 | Rocket League Town Hall',
        'March 8 | IHSEA Coaches & Parents Town Hall',
        'April 12 | Overwatch Town Hall',
        'May 10 | End of Year Town Hall',
    ];

    const btnText = `IHSEA`;
    const btnLink = `https://ihsea.org`;

    return (
        <Layout seoOverride={seoOverride}>
            <PartnersHeaderTabs
                tabOneTitle={'General'}
                tabOneRoute={'/partners/ihsea'}
                tabTwoTitle={'Recruiting'}
                tabTwoRoute={'/partners/ihsea/recruiting'}
                tabThreeTitle={'Rankings'}
                tabThreeRoute={'/partners/ihsea/rankings'}
                imgUrl={`/images/ind-partners/ihsea-logo.png`}
                imgWidth={560}
                imgHeight={208}
            />
            <PartDescr label={'Our Partnership'} description={descrOne} btnText={btnText} btnLink={btnLink} />
            <QuoteSection
                className="ihsea-quote"
                quoteTwo={
                    'Our mission has always been to provide a clear pathway of opportunity for students that are passionate about esports. That pathway can lead to college, to the workforce or both. Our partnership with IHSEA really helps move those initiatives forward. We have seen the great work the team at IHSEA has done, and we hope to help amplify their goals and offerings by connecting students, parents and coaches with colleges throughout the country to help students find the right fit for them. We are thankful for the opportunity to work side by side with a group so passionate and talented with similar objectives that align well with Stay Plugged In.'
                }
                personTwo={'Rick Suarez, Managing Director of Stay Plugged In'}
                quote={`The Illinois High School Esports Association is excited to be working with Stay Plugged In. The work that SPIN has done for the scholastic ecosystem has been incredibly positive, and we cannot wait to bring their offerings specifically to our Illinois community. Our state has no shortage of bright and talented students that deserve the option to evaluate nationwide collegiate esports offers, and Stay Plugged In will help those students reach those institutions.`}
                person={'Andy Mendez, Executive Director of IHSEA'}
            />
            <div className="container">
                {months && <CalendarSection months={months} twitchSlug={`ihsea`} />}
                {!months && <p>Calendar Coming Soon!</p>}
            </div>
            <ActivationSection
                description={descrAct1}
                header="Online Recruiting Event"
                items={items}
                items2={items2}
                dark={true}
                icon={<ShowcaseIcon className="showcase-white" />}
                columnOneTitle={'Fall Showcase'}
                columnTwoTitle={'Spring Showcase'}
                ctaBtn={true}
            />
            <ActivationSection
                description={descrPanel}
                header="Recruiting Panels"
                items={itemsPanel}
                items2={itemsPanel2}
                icon={<PanelIcon className="panel-dark" />}
                columnOneTitle={'Fall Panels'}
                columnTwoTitle={'Spring Panels'}
            />
        </Layout>
    );
}

export async function getStaticProps(ctx) {
    return UrqlSSGForPage(OrgIhseaIndexDocument);
}
